import { useState, useCallback } from "react";

/**
 * Хук для работы с настройками читалки в localStorage
 */
export default function useReaderSettings() {
  // Утилиты для работы с localStorage
  const getReaderSettings = useCallback(() => {
    try {
      const settings = localStorage.getItem("bookReaderSettings");
      return settings ? JSON.parse(settings) : {};
    } catch {
      return {};
    }
  }, []);

  const saveReaderSettings = useCallback(
    (newSettings) => {
      try {
        const currentSettings = getReaderSettings();
        const updatedSettings = { ...currentSettings, ...newSettings };
        localStorage.setItem(
          "bookReaderSettings",
          JSON.stringify(updatedSettings)
        );
      } catch (error) {
        console.warn("Failed to save reader settings:", error);
      }
    },
    [getReaderSettings]
  );

  // Состояние настроек с инициализацией из localStorage
  const [settings, setSettings] = useState(() => {
    const savedSettings = getReaderSettings();
    return {
      fontSize: savedSettings.fontSize || 16,
      readerTheme: savedSettings.readerTheme || "auto",
      spacing: savedSettings.spacing || 1.4,
      justify:
        savedSettings.justify !== undefined ? savedSettings.justify : true,
      hyphenate:
        savedSettings.hyphenate !== undefined ? savedSettings.hyphenate : true,
      ...savedSettings,
    };
  });

  // Функция для обновления настроек
  const updateSettings = useCallback(
    (newSettings) => {
      setSettings((prevSettings) => {
        const updatedSettings = { ...prevSettings, ...newSettings };
        // Сохраняем в localStorage
        saveReaderSettings(newSettings);
        return updatedSettings;
      });
    },
    [saveReaderSettings]
  );

  // Функции для обновления отдельных настроек
  const updateFontSize = useCallback(
    (fontSize) => {
      updateSettings({ fontSize });
    },
    [updateSettings]
  );

  const updateTheme = useCallback(
    (readerTheme) => {
      updateSettings({ readerTheme });
    },
    [updateSettings]
  );

  const updateSpacing = useCallback(
    (spacing) => {
      updateSettings({ spacing });
    },
    [updateSettings]
  );

  const updateJustify = useCallback(
    (justify) => {
      updateSettings({ justify });
    },
    [updateSettings]
  );

  const updateHyphenate = useCallback(
    (hyphenate) => {
      updateSettings({ hyphenate });
    },
    [updateSettings]
  );

  return {
    // Текущие настройки
    settings,

    // Основные функции
    updateSettings,
    getReaderSettings,
    saveReaderSettings,

    // Удобные функции для отдельных настроек
    updateFontSize,
    updateTheme,
    updateSpacing,
    updateJustify,
    updateHyphenate,

    // Отдельные значения для удобства
    fontSize: settings.fontSize,
    readerTheme: settings.readerTheme,
    spacing: settings.spacing,
    justify: settings.justify,
    hyphenate: settings.hyphenate,
  };
}
